import {
  Box,
  Image,
  PhotoGallery,
  TextField,
  Typography,
} from "@avenue-8/sidekick-ui";
import { HeaderSectionView } from "@cma/presentation-generation-logic";
import { HeaderBrandingSectionView } from "@cma/presentation-generation-logic/sections/header-branding/header-branding-section-view";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { ImagesCarousel } from "./components/ImagesCarousel";
import { PresentationSectionProps } from "../PresentationSectionProps";
import {
  HeaderBrandingSectionConfig,
  HeaderSectionConfig,
} from "@cma/presentation-generation-logic/models/section-config";
import { NumericFormat } from "react-number-format";
import { PhotosUploader } from "@cma/shared/components/PhotosUploader";
import { SinglePhotoUploader } from "@cma/shared/components/SinglePhotoUploader";
import { strToNumericStr } from "@cma/shared/helpers/form-converters";
import { compressImage } from "@shared/utils/compress-image";
import { useUploadSubjectPropertyPhotos } from "@cma/shared/queries/cma/useUploadSubjectPropertyPhotos";
import { useUploadPresentationSectionImage } from "@cma/shared/queries/cma/useUploadPresentationSectionImage";
import { useCallback, useEffect, useRef, useState } from "react";
import { SwiperRef } from "swiper/react";
import { PropertyPhoto } from "@cma/presentation-generation-logic/models";
import { useDeleteSubjectPropertyPhoto } from "@cma/shared/queries/cma/useDeleteSubjectPropertyPhoto";
import { createId } from "@paralleldrive/cuid2";
import DeleteIcon from "@mui/icons-material/Delete";
import PanoramaIcon from "@mui/icons-material/Panorama";
import { toast } from "react-toastify";
import { useMakeSubjectPropertyCoverPhoto } from "@cma/shared/queries/cma/useMakeSubjectPropertyCoverPhoto";
import {
  AddCoverPhotoContainer,
  CoverPhotoContainer,
  PreparedForContainer,
} from "./HeroHeader.styles";
import { EditableInput } from "../../shared/EditableInput";
import { EditableText } from "../../shared/EditableText";
import { PresentationNavbar } from "../../shared/PresentationNavbar";
import { textFormatter } from "@shared/utils/text-formatter";
import { useUIStore } from "@cma/store/ui.store";

type Props = PresentationSectionProps<
  HeaderSectionView | HeaderBrandingSectionView,
  HeaderSectionConfig | HeaderBrandingSectionConfig
>;

const DAYS_ON_THE_MARKET_ENABLED = false;

const isAllowedPrice = (values: any) => {
  const { floatValue } = values;
  if (floatValue === undefined) return true;
  return floatValue <= 999_999_999;
};

export const HeroHeaderEdit = (props: Props) => {
  const { sectionView, sectionConfig, sourceData, onUpdateSection } = props;
  const chatWidgetConfirmDialog = useUIStore(
    (state) => state.chatWidgetConfirmDialog,
  );
  const {
    id,
    type,
    clientName,
    addressLine1,
    addressLine2,
    city,
    state,
    zipCode,
    photos,
    minSellPrice,
    maxSellPrice,
    daysOnMarket,
  } = sectionView;
  const [photosState, setPhotosState] = useState<PropertyPhoto[]>(photos || []);
  const [photoGalleryIndex, setPhotoGalleryIndex] = useState(-1);
  const [isEditingClientName, setIsEditingClientName] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const swiperRef = useRef<SwiperRef>(null);
  const coverPhoto = photosState[0];

  const uploadPhotosMutation = useUploadSubjectPropertyPhotos({
    onError: () => {
      setPhotosState((prev) => {
        return prev.filter((photo) => {
          if (photo.id?.startsWith("temp-")) {
            URL.revokeObjectURL(photo.url);
            return false;
          }
          return true;
        });
      });
    },
  });

  const deletePhotoMutation = useDeleteSubjectPropertyPhoto({
    onError: () => {
      toast.error("Failed to delete photo");
    },
  });
  const makeCoverPhotoMutation = useMakeSubjectPropertyCoverPhoto({
    onSuccess: () => {
      toast.success("Cover photo updated successfully");
    },
    onError: () => {
      toast.error("Failed to make cover photo");
    },
  });

  useEffect(() => {
    setPhotosState(photos || []);
  }, [photos]);

  const scrollToCarouselEnd = () => {
    if (swiperRef.current) {
      const swiper = swiperRef.current.swiper;
      swiper.slideTo(swiper.slides.length - 1);
    }
  };

  useEffect(
    () => () => {
      photosState.forEach((photo) => {
        if (photo.url) URL.revokeObjectURL(photo.url);
      });
    },
    [photosState],
  );

  const handlePriceBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    priceType: "min" | "max",
  ) => {
    const rawValue = strToNumericStr(event.target.value);
    const numericValue = rawValue ? Number(rawValue) : null;

    const currentValue = priceType === "min" ? minSellPrice : maxSellPrice;

    if (numericValue !== currentValue) {
      onUpdateSection({
        sectionConfig,
        editableSourceData: {
          priceRange: {
            min: priceType === "min" ? numericValue : minSellPrice,
            max: priceType === "max" ? numericValue : maxSellPrice,
          },
        },
      });
    }
  };

  const handleUploadPhotos = useCallback(
    async (files: File[]) => {
      const optimisticPhotos = files.map((file) => ({
        id: `temp-${createId()}`,
        url: URL.createObjectURL(file),
        alt: file.name,
        caption: file.name,
      }));
      const tempIds = new Set(optimisticPhotos.map((p) => p.id));
      setPhotosState((prev) => [...prev, ...optimisticPhotos]);

      setIsCompressing(true);
      const filesToUpload = await Promise.all(
        files.map((file) => compressImage(file)),
      );
      setIsCompressing(false);

      const formData = new FormData();
      filesToUpload.forEach((file) => {
        formData.append("images", file);
      });

      const uploadedPhotos = await uploadPhotosMutation.mutateAsync({
        cmaId: sourceData.id,
        formData,
      });

      setPhotosState((prev) => [
        ...prev.filter((p) => !tempIds.has(p.id ?? "")),
        ...uploadedPhotos,
      ]);

      return uploadedPhotos;
    },
    [sourceData.id, uploadPhotosMutation],
  );

  const handleDeleteImage = useCallback(
    async (photo: PropertyPhoto) => {
      if (!photo.id) {
        toast.error("This photo can't be deleted yet.");
        return;
      }
      setPhotosState((prev) => prev.filter((p) => p.id !== photo.id));
      deletePhotoMutation.mutate({
        cmaId: sourceData.id,
        imgId: Number(photo.id),
      });
    },
    [deletePhotoMutation, sourceData.id],
  );

  const handleMakeCoverPhoto = useCallback(
    async (photo: PropertyPhoto) => {
      await makeCoverPhotoMutation.mutateAsync({
        cmaId: sourceData.id,
        imgId: Number(photo.id),
      });
    },
    [makeCoverPhotoMutation, sourceData.id],
  );

  const uploadCoverPhotoMutation = useUploadPresentationSectionImage({
    onSuccess: (data) => {
      if (type === "header-branding") {
        onUpdateSection({
          sectionConfig: {
            ...(sectionConfig as HeaderBrandingSectionConfig),
            officeLogo: data.url,
          },
        });
      }
    },
  });

  const uploadOfficeLogo = useCallback(
    async (file: File) => {
      const compressed = await compressImage(file, { preset: "sm" });
      uploadCoverPhotoMutation.mutate({
        id: sourceData.id,
        sectionId: sectionConfig.id,
        file: compressed,
      });
    },
    [uploadCoverPhotoMutation, sectionConfig.id, sourceData.id],
  );

  const deleteOfficeLogo = useCallback(() => {
    if (sectionConfig.type === "header-branding") {
      onUpdateSection({
        sectionConfig: {
          ...sectionConfig,
          officeLogo: null,
        },
      });
    }
  }, [onUpdateSection, sectionConfig]);

  const handleUploadCoverPhoto = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const uploadedPhotos = await handleUploadPhotos(files);
      if (!uploadedPhotos || uploadedPhotos.length === 0) return;

      const newCoverPhoto = uploadedPhotos[uploadedPhotos.length - 1];
      if (!newCoverPhoto.id) return;

      setPhotosState((prev) => [
        newCoverPhoto,
        ...prev.filter((p) => p.id !== newCoverPhoto.id),
      ]);

      await makeCoverPhotoMutation.mutateAsync({
        cmaId: sourceData.id,
        imgId: Number(newCoverPhoto.id),
      });
    },
    [handleUploadPhotos, makeCoverPhotoMutation, sourceData.id],
  );

  const handleDeleteCoverPhoto = useCallback(async () => {
    if (!coverPhoto?.id) return;

    const confirmed = await chatWidgetConfirmDialog({
      title: "Delete Photo",
      content: "Are you sure you want to delete this photo?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (!confirmed) return;

    handleDeleteImage(coverPhoto);
  }, [coverPhoto, chatWidgetConfirmDialog, handleDeleteImage]);

  const onUploadPhotos = useCallback(
    async (files: File[]) => {
      handleUploadPhotos(files);
      setTimeout(() => {
        scrollToCarouselEnd();
      }, 100);
    },
    [handleUploadPhotos],
  );

  const officeLogo = type === "header-branding" ? sectionView.officeLogo : null;

  return (
    <Box id={id} pb={6} px={3} textAlign="center">
      <PresentationNavbar
        agent={sourceData.agent}
        officeLogo={officeLogo}
        editMode={true}
        onUploadOfficeLogo={uploadOfficeLogo}
        onDeleteOfficeLogo={officeLogo ? deleteOfficeLogo : undefined}
        isLoadingOfficeLogo={uploadCoverPhotoMutation.isPending}
      />

      <PreparedForContainer>
        <Typography>Prepared for</Typography>
        <Box sx={{ width: "fit-content" }}>
          {!isEditingClientName && clientName ? (
            <EditableText
              sx={{ fontWeight: "500" }}
              onClick={() => setIsEditingClientName(true)}
            >
              {clientName}
            </EditableText>
          ) : (
            <EditableInput
              value={clientName}
              type="text"
              name="clientName"
              placeholder="Enter client name"
              autoFocus={isEditingClientName}
              styles={{ maxWidth: "160px" }}
              onSave={(newClientName) => {
                onUpdateSection({
                  sectionConfig,
                  editableSourceData: {
                    clientDisplayName: newClientName,
                  },
                });
                setIsEditingClientName(false);
              }}
              onCancel={() => setIsEditingClientName(false)}
            />
          )}
        </Box>
      </PreparedForContainer>

      <Typography variant="h3" m={3}>
        {textFormatter.formatFullAddress({
          addressLine1,
          addressLine2,
          city,
          state,
          zipCode,
        })}
      </Typography>

      <SinglePhotoUploader
        onFileAccepted={(file) => handleUploadCoverPhoto([file])}
        onFileRemoved={coverPhoto ? handleDeleteCoverPhoto : undefined}
        maxSize={5 * 1024 * 1024}
        isLoading={uploadPhotosMutation.isPending}
        serverError={uploadPhotosMutation.error}
        resetServerError={uploadPhotosMutation.reset}
      >
        <CoverPhotoContainer>
          {coverPhoto?.url ? (
            <Image
              src={coverPhoto.url}
              alt={"Cover photo"}
              key={coverPhoto.url}
            />
          ) : (
            <AddCoverPhotoContainer>
              <PhotoCameraIcon fontSize="large" />
            </AddCoverPhotoContainer>
          )}
        </CoverPhotoContainer>
      </SinglePhotoUploader>

      <Box
        mt={3}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <Typography mb={1}>
          In today’s market, this home should sell for between
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <NumericFormat
            customInput={TextField}
            placeholder="Enter low price"
            sx={{ maxWidth: 160 }}
            hideHelperTextSpace
            value={minSellPrice || ""}
            thousandSeparator
            prefix="$"
            decimalScale={0}
            allowNegative={false}
            isAllowed={isAllowedPrice}
            onBlur={(e) => handlePriceBlur(e, "min")}
          />
          <Typography>and</Typography>
          <NumericFormat
            customInput={TextField}
            placeholder="Enter high price"
            sx={{ maxWidth: 160 }}
            hideHelperTextSpace
            value={maxSellPrice || ""}
            thousandSeparator
            prefix="$"
            decimalScale={0}
            allowNegative={false}
            isAllowed={isAllowedPrice}
            onBlur={(e) => handlePriceBlur(e, "max")}
          />
        </Box>
      </Box>

      {DAYS_ON_THE_MARKET_ENABLED && daysOnMarket > 0 && (
        <Box
          mt={4}
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
        >
          <Typography variant={"overline"}>
            {daysOnMarket} days on the market
          </Typography>
        </Box>
      )}

      <PhotosUploader
        multiple
        maxFiles={10}
        onFilesAccepted={onUploadPhotos}
        maxSize={10 * 1024 * 1024} // 10MB
        isLoading={uploadPhotosMutation.isPending || isCompressing}
        serverError={uploadPhotosMutation.error}
        resetServerError={uploadPhotosMutation.reset}
      >
        <ImagesCarousel
          photos={photosState}
          ref={swiperRef}
          onClickImage={setPhotoGalleryIndex}
          editMode
        />
      </PhotosUploader>
      <PhotoGallery
        photos={photosState || []}
        initialIndex={photoGalleryIndex}
        open={photoGalleryIndex > -1}
        onClose={() => setPhotoGalleryIndex(-1)}
        actions={[
          {
            text: "Delete",
            icon: () => <DeleteIcon fontSize="large" />,
            onClick: handleDeleteImage,
            variant: "outlined",
            isLoading: deletePhotoMutation.isPending,
          },
          {
            text: "Make cover photo",
            icon: () => <PanoramaIcon fontSize="large" />,
            onClick: handleMakeCoverPhoto,
            variant: "contained",
            isLoading: makeCoverPhotoMutation.isPending,
          },
        ]}
      />
    </Box>
  );
};
